import { ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { Formik } from 'formik'

import { getUnprocessableErrors } from '@10xlabs/utils'
import { Button } from 'components/v2/Button'
import FormLayout from 'components/v2/FormLayout'
import { DriverDocumentsFormCard } from 'features/Drivers/components/DriverDocumentsCard'
import { driverStep2Schema } from 'features/Drivers/components/NewDrivers/Step2/schema'
import { INewDriverStep2 } from 'features/Drivers/types'
import { GTTDriver } from 'gql/types.generated'
import { usePushFileToS3 } from 'utils/file'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { mapDriverToStep2Values } from 'features/Drivers/utils'
import { isEmpty } from 'lodash'
import { safeFormatMessage } from 'utils/intl'
import { documentsMessages } from 'features/Drivers/components/DriverDocumentsCard/messages'
import { useMutation } from '@apollo/client'
import { CHANGE_DRIVER_DOCUMENTS } from 'features/Drivers/mutations-legacy'

type FullfilledPromise = {
  status: 'fulfilled'
  value: string
  category: string
  file: '' | File
}

export const NewDriversStep2 = ({
  driver,
  isEdit,
  onSubmit,
  onCancel
}: {
  driver?: GTTDriver
  isEdit?: boolean
  onSubmit: () => void
  onCancel: () => void
}): ReactElement => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const pushFileToS3 = usePushFileToS3()
  const [s3Error, setS3Error] = useState<string | undefined>()
  const driverId = searchParams.get('driverId') || driver?.id || ''

  const [changeDriverDocuments, { error: changeDriverDocumentsError }] = useMutation(CHANGE_DRIVER_DOCUMENTS)

  const addDocuments = async (documents: INewDriverStep2['documents']): Promise<boolean> => {
    const filledDocuments = documents.filter(({ file }) => file && file instanceof File)
    if (isEmpty(filledDocuments)) return true

    const uploads = filledDocuments.map(({ file }) => pushFileToS3(file as File))

    const uploadResults = await Promise.allSettled(uploads)
    const documentsWithResult = filledDocuments.map((document, index) => ({
      ...document,
      ...uploadResults[index]
    }))
    const rejectedDocuments = documentsWithResult.filter(({ status }) => status === 'rejected')
    const acceptedDocuments = documentsWithResult.filter(({ status }) => status === 'fulfilled') as FullfilledPromise[]

    if (rejectedDocuments.length > 0) {
      const rejectedDocumentsMessages = rejectedDocuments
        .map(({ category }) => {
          return safeFormatMessage({ intl, messages: documentsMessages, key: category })
        })
        .join(', ')

      setS3Error(rejectedDocumentsMessages)
      return false
    }

    if (acceptedDocuments.length > 0) {
      return changeDriverDocuments({
        variables: {
          input: {
            driverID: driverId,
            documents: acceptedDocuments.map((document) => ({
              category: document.category,
              filePath: document.value
            }))
          }
        }
      })
        .then(() => true)
        .catch(() => false)
    }

    return false
  }

  const handleSubmit = async (
    values: INewDriverStep2,
    { setSubmitting }: { setSubmitting: (bool: boolean) => void }
  ): Promise<void> => {
    setS3Error(undefined)

    const documentsUploadResult = await addDocuments(values.documents)

    setSubmitting(false)
    if (documentsUploadResult) {
      onSubmit()
    }
  }

  const initialValues: INewDriverStep2 = mapDriverToStep2Values(driver)

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={driverStep2Schema()}>
      {({ handleSubmit, dirty, isSubmitting }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <DriverDocumentsFormCard />
            {changeDriverDocumentsError && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(changeDriverDocumentsError)}>
                  <FormattedMessage
                    id="partners.new.documents.error"
                    defaultMessage="There was an error while adding the documents"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            {s3Error && (
              <FormLayout.Actions>
                <ErrorMessage>
                  <FormattedMessage
                    id="partners.new.uploadDocuments.error"
                    defaultMessage="There was an error while uploading the following document(s): {documents}"
                    values={{ documents: s3Error }}
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.back" defaultMessage="Back" />
              </Button>
              <Button type="submit" kind="primary" disabled={!dirty && isEdit} loading={isSubmitting}>
                <FormattedMessage id="partners.new.step3.save" defaultMessage="Save" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
